import React, { useState, useEffect, useRef } from "react";

const fieldDefs = [
  {
    name: "name",
    label: "Full Name",
    type: "text",
    icon: "👤",
    placeholder: "Enter full name",
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    icon: "✉️",
    placeholder: "Enter email",
  },
  {
    name: "phone",
    label: "Phone Number",
    type: "tel",
    icon: "📞",
    placeholder: "Enter phone number",
  },
  {
    name: "country",
    label: "Preferred Country",
    type: "text",
    icon: "🌍",
    placeholder: "Enter preferred country",
  },
];

export default function AdmissionEnquiryModal({
  open,
  onClose,
  form,
  setForm,
  onSubmit,
  submitting,
  message,
}) {
  const [submitted, setSubmitted] = useState(false);
  const [messageDuringOpen, setMessageDuringOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const prevMessageRef = useRef(undefined);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setMessageDuringOpen(false);

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      return;
    }

    prevMessageRef.current = message;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [open, message]);

  useEffect(() => {
    if (open && message && message !== prevMessageRef.current) {
      setMessageDuringOpen(true);
    }

    prevMessageRef.current = message;
  }, [message, open]);

  useEffect(() => {
    const updateViewport = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);

    return () => {
      window.removeEventListener("resize", updateViewport);
    };
  }, []);

  const handleClose = () => {
    setSubmitted(false);
    setMessageDuringOpen(false);

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await onSubmit(e);

      // Show thank you screen
      setSubmitted(true);
      setMessageDuringOpen(true);

      // Automatically close modal after thank you appears
      setTimeout(() => {
        setSubmitted(false);
        setMessageDuringOpen(false);

        if (onClose) {
          onClose();
        }
      }, 1000);
    } catch (err) {
      console.error("Form submission failed", err);
    }
  };

  if (!open) return null;

  const renderFormContent = (compact = false) => (
    <div
      className={
        compact ? "w-full max-w-[420px] pt-0 p-4" : "w-full max-w-xl pt-0"
      }
    >
      {submitted || messageDuringOpen ? (
        <div className="flex min-h-[360px] mt-10 sm:mt-32 flex-col items-center justify-center text-center sm:min-h-[420px]">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
            <svg
              className="h-12 w-12 text-green-600"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 12.5l2.5 2.5L17 8"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h4 className="mt-5 text-3xl font-bold text-[#ff9245]">
            Thank you!
          </h4>

          <p className="mt-3 text-base text-gray-600">
            We will contact you soon.
          </p>

          {message && <p className="mt-2 text-sm text-gray-500">{message}</p>}
        </div>
      ) : (
        <>
          <div className="mb-4 text-center">
            <h3
              className={
                compact
                  ? "mx-auto max-w-[300px] text-[19px] font-extrabold leading-tight text-[#1c2d44]"
                  : "mx-auto max-w-[320px] text-[20px] font-extrabold leading-tight text-[#1c2d44] sm:max-w-none"
              }
            >
              Start Your Journey of MBBS Abroad With{" "}
              <span className="mt-0.5 block text-[#ff9245] sm:mt-0 sm:inline">
                NeetSupport
              </span>
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            {fieldDefs.map((field) => (
              <div key={field.name}>
                <label
                  className={
                    compact
                      ? "mb-1 block text-[14px] font-semibold text-[#1f2937]"
                      : "mb-1 block text-[15px] font-semibold text-[#1f2937]"
                  }
                >
                  <span className="mr-2 text-[18px]">{field.icon}</span>{" "}
                  {field.label}
                </label>

                <div
                  className={
                    compact
                      ? "flex h-10 items-center rounded-2xl border border-[#d8dee8] bg-white px-3 transition focus-within:border-[#ff9245] focus-within:ring-2 focus-within:ring-[#ff9245]/15 sm:h-12 sm:px-4"
                      : "flex h-11 items-center rounded-2xl border border-[#d8dee8] bg-white px-3.5 transition focus-within:border-[#ff9245] focus-within:ring-2 focus-within:ring-[#ff9245]/15 sm:h-12 sm:px-4"
                  }
                >
                  <input
                    name={field.name}
                    type={field.type}
                    value={form?.[field.name] || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        [e.target.name]: e.target.value,
                      })
                    }
                    required
                    placeholder={field.placeholder}
                    className={
                      compact
                        ? "w-full bg-transparent text-[15px] text-[#334155] outline-none placeholder:text-[#9aa3b2] sm:text-[16px]"
                        : "w-full bg-transparent text-[16px] text-[#334155] outline-none placeholder:text-[#9aa3b2]"
                    }
                  />
                </div>
              </div>
            ))}

            <button
              type="submit"
              disabled={submitting}
              className={
                compact
                  ? "mt-4 h-11 w-full rounded-2xl bg-[#ff9245] text-[16px] font-extrabold leading-none text-white shadow-[0_8px_18px_rgba(249,146,69,0.32)] transition-all duration-200 hover:bg-[#f47f2d] hover:shadow-[0_10px_20px_rgba(249,146,69,0.38)] active:translate-y-[1px] active:shadow-[0_6px_12px_rgba(249,146,69,0.28)] disabled:cursor-not-allowed disabled:opacity-70 sm:h-12 sm:text-[18px]"
                  : "mt-4 h-12 w-full rounded-2xl bg-[#ff9245] text-[18px] font-extrabold leading-none text-white shadow-[0_8px_18px_rgba(249,146,69,0.32)] transition-all duration-200 hover:bg-[#f47f2d] hover:shadow-[0_10px_20px_rgba(249,146,69,0.38)] active:translate-y-[1px] active:shadow-[0_6px_12px_rgba(249,146,69,0.28)] disabled:cursor-not-allowed disabled:opacity-70"
              }
            >
              {submitting ? "Submitting..." : "Submit Enquiry"}
            </button>
          </form>
        </>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-4 backdrop-blur-sm md:px-12 md:py-8">
      <div
        className="absolute inset-0"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        className={
          isMobileView
            ? "relative w-full max-w-[430px] overflow-hidden rounded-3xl bg-white shadow-2xl"
            : "relative w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl"
        }
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-0 top-0 z-30 flex h-11 w-11 items-center justify-center text-3xl font-bold text-[#ff9245] transition"
        >
          ×
        </button>

        {isMobileView ? (
          <div className="max-h-[94vh] overflow-y-auto px-4 py-5">
            {renderFormContent(true)}
          </div>
        ) : (
          <div className="grid max-h-[94vh] min-h-[560px] grid-cols-1 overflow-y-auto lg:grid-cols-2 lg:overflow-hidden">
            <div className="relative hidden min-h-[300px] items-center justify-center overflow-hidden bg-white lg:flex lg:min-h-[560px]">
              <img
                src="/assets/img/ug_abroad/mbbsposter.webp"
                alt="MBBS Abroad Counselling"
                className="h-full w-full object-cover object-center"
              />
            </div>

            <div className="flex items-start justify-center bg-white px-5 py-6 sm:px-7 sm:py-7 md:px-10 md:py-8">
              {renderFormContent(false)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}